import React, { useMemo } from 'react';
import { ResponsiveBar } from '@nivo/bar';
import { RootCauseGenericBarGraphGroupResult } from 'libs/end-to-end/src/root-cause-analysis';
import { capitalize } from 'lodash';
import { KARLA_FONT_FAMILY } from 'libs/end-to-end/src/types/branding/fonts';

export const GenericBarChartTooltip: React.FC<{
  text: string;
}> = (props) => {
  const { text } = props;
  return (
    <div className="p-4 font-sans text-left bg-white rounded-md filter shadow-8px bg-opacity-70 backdrop-blur-sm">
      <p className="font-sans text-xs font-bold ">{text}</p>
    </div>
  );
};

export const GenericBarChart: React.FC<{
  result: RootCauseGenericBarGraphGroupResult;
  yAxisText: string;
  demographicKey: string;
  controlVariable: string;
  colors: string[];
}> = ({ result, yAxisText, demographicKey, colors }) => {
  const yAxisTextCapitalized = capitalize(yAxisText);

  const data: Record<string, number> = useMemo(() => {
    const a: Record<string, number> = {};

    const datumCount = result.y.length;

    for (let i = 0; i < datumCount; i++) {
      a[result.x[i]] = result.y[i];
    }

    return a;
  }, [result]);

  const keys = useMemo(() => {
    return Object.keys(data);
  }, [data]);

  const maxDisparity = useMemo(() => {
    return Math.max(...result.y);
  }, [result]);

  const minDisparity = useMemo(() => {
    return Math.min(...result.y);
  }, [result]);

  const magnitude = useMemo(() => {
    return Math.max(Math.abs(maxDisparity), Math.abs(minDisparity)) * 1.1;
  }, [maxDisparity, minDisparity]);

  const getYValueText = (value: string) => {
    if (result.visualizationAttributes?.displayAsPercentage) {
      return `${value}%`;
    }

    // TODO: This is temporary to ensure we use the $ sign when the yAxisText contains wage or compensation
    if (yAxisText.includes('wage') || yAxisText.includes('compensation')) {
      return `$${value}`;
    }

    return `${value}`;
  };

  return (
    <ResponsiveBar
      data={[data]}
      groupMode="grouped"
      valueFormat={(percentage: number) => `${percentage}`}
      margin={{ top: 10, right: 20, bottom: 50, left: 100 }}
      minValue={-magnitude}
      maxValue={magnitude}
      borderRadius={3}
      colors={colors}
      colorBy="id"
      label=""
      padding={0.25}
      innerPadding={3}
      keys={keys}
      indexBy={demographicKey}
      enableGridX={false}
      axisLeft={{
        legend: yAxisTextCapitalized,
        legendOffset: -70,
        legendPosition: 'middle',
        tickPadding: 10,
        tickSize: 0,
        format: (value: string) => {
          return getYValueText(value);
        },
      }}
      tooltip={(d) => {
        return (
          <GenericBarChartTooltip text={getYValueText(d.value.toFixed(2))} />
        );
      }}
      // Hide the bottom axis.
      axisBottom={{
        tickPadding: 0,
        tickSize: 0,
        format: () => ``,
      }}
      theme={{
        text: {
          fontFamily: KARLA_FONT_FAMILY,
          fontSize: 16,
        },
        axis: {
          domain: {
            line: {
              stroke: '#B8BABC',
              strokeWidth: 2,
            },
          },
          ticks: {
            line: {
              strokeWidth: 0,
            },
          },
          legend: {
            text: {
              stroke: '#791fd1',
              fontSize: 16,
              strokeWidth: 0.2,
            },
          },
        },
      }}
    />
  );
};
